/**
 * Purchase Transactions List Page Component
 * /admin/purchases
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import apiClient from '../api/client';

const Purchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.getVehiclePurchases({ search: searchTerm });
      // Handle paginated response
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setPurchases(data);
    } catch (err) {
      setError(err.message || 'Failed to load purchase transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadPurchases();
  };

  const getBuyerTypeLabel = (type) => {
    const labels = {
      investor: 'Investor',
      driver: 'Driver',
    };
    return labels[type] || type;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      paid: 'success',
      partial: 'info',
      overdue: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading && purchases.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Purchase Transactions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/purchases/new')}
        >
          Add Purchase
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <form onSubmit={handleSearchSubmit}>
          <TextField
            fullWidth
            placeholder="Search purchases by vehicle, buyer..."
            value={searchTerm}
            onChange={handleSearch}
            slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            } }}
          />
        </form>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Transaction Date</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell>Buyer Type</TableCell>
              <TableCell>Buyer</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Payment Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No purchase transactions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              purchases.map((purchase) => (
                <TableRow key={purchase.id} hover>
                  <TableCell>
                    {purchase.transaction_date
                      ? new Date(purchase.transaction_date).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {purchase.vehicle_plate_number || purchase.vehicle || '-'}
                  </TableCell>
                  <TableCell>
                    {purchase.buyer_type ? getBuyerTypeLabel(purchase.buyer_type) : '-'}
                  </TableCell>
                  <TableCell>
                    {purchase.buyer_name ||
                      (purchase.buyer_investor_name || purchase.buyer_investor) ||
                      (purchase.buyer_driver_name || purchase.buyer_driver) ||
                      '-'}
                  </TableCell>
                  <TableCell>
                    {purchase.purchase_amount
                      ? `${purchase.purchase_amount.toLocaleString()} AED`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={purchase.payment_status || 'pending'}
                      color={getPaymentStatusColor(purchase.payment_status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/purchases/${purchase.id}`)}
                      title="View"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/purchases/${purchase.id}/edit`)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Purchases;

